import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { toast } from 'sonner';

export interface AutomationJob {
  id: string;
  job_name: string;
  base_prompt: string;
  prompt_variations: any;
  models_to_use: any;
  total_images_target: number;
  images_generated: number;
  size: string;
  quality: string;
  style: string;
  n: number;
  status: string;
  start_time: string | null;
  end_time: string | null;
  pause_time: string | null;
  rpm_limit: number;
  requests_made_this_minute: number;
  last_request_time: string | null;
  success_count: number;
  error_count: number;
  last_error: string | null;
  retry_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  selected_api_key_id: string | null;
}

export const useAutomationJobs = () => {
  const { user } = useSecureAuthContext();
  const [jobs, setJobs] = useState<AutomationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const rateLimitUntilRef = useRef<Map<string, number>>(new Map());

  const fetchJobs = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('automated_generation_jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load automation jobs');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchJobs();

    // Set up realtime subscription
    const channel = supabase
      .channel('automation-jobs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automated_generation_jobs'
        },
        (payload) => {
          console.log('Job update:', payload);
          fetchJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchJobs]);

  const createJob = async (jobData: {
    job_name: string;
    base_prompt: string;
    prompt_variations?: string[];
    models_to_use: string[];
    total_images_target: number;
    size?: string;
    quality?: string;
    style?: string;
    n?: number;
    rpm_limit?: number;
    selected_api_key_id?: string | null;
  }) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('automated_generation_jobs')
        .insert({
          ...jobData,
          created_by: user.id,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Automation job created successfully');
      fetchJobs();
      return data;
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error('Failed to create automation job');
      return null;
    }
  };

  const startJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'running',
          start_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job started');
      fetchJobs();
      
      // Start processing loop
      processJobContinuously(jobId);
    } catch (error) {
      console.error('Error starting job:', error);
      toast.error('Failed to start job');
    }
  };

  const processJobContinuously = async (jobId: string) => {
    setProcessing(prev => ({ ...prev, [jobId]: true }));

    const interval = setInterval(async () => {
      try {
        // Check job status
        const { data: job } = await supabase
          .from('automated_generation_jobs')
          .select('status, images_generated, total_images_target')
          .eq('id', jobId)
          .single();

        if (!job || job.status !== 'running') {
          clearInterval(interval);
          setProcessing(prev => ({ ...prev, [jobId]: false }));
          return;
        }

        if (job.images_generated >= job.total_images_target) {
          clearInterval(interval);
          setProcessing(prev => ({ ...prev, [jobId]: false }));
          return;
        }
        // Respect per-job backoff if set from prior 429
        const until = rateLimitUntilRef.current.get(jobId);
        if (until && Date.now() < until) {
          const waitMs = until - Date.now();
          console.log(`⏳ Job ${jobId} backing off for ${Math.ceil(waitMs/1000)}s (429)`);
          return; // skip this tick
        }

        // Call edge function to process next batch
        const { error, data } = await supabase.functions.invoke('process-automation-job', {
          body: { jobId }
        });
        if (error) {
          const anyErr: any = error;
          
          // Parse error body to check for job status
          let errorBody: any = null;
          try {
            errorBody = typeof anyErr?.context?.body === 'string' ? JSON.parse(anyErr.context.body) : anyErr?.context?.body;
          } catch {}
          
          // Handle stopped/paused/cancelled jobs gracefully (not an error)
          if (errorBody?.status === 'stopped' || errorBody?.status === 'paused' || errorBody?.status === 'cancelled') {
            console.log(`⏸️ Job ${jobId} is ${errorBody.status}, stopping processing loop`);
            clearInterval(interval);
            setProcessing(prev => ({ ...prev, [jobId]: false }));
            return;
          }
          
          // Handle rate limit errors
          let waitSeconds: number | null = null;
          if (errorBody?.wait_seconds != null) {
            waitSeconds = Number(errorBody.wait_seconds);
          }
          if (!waitSeconds) {
            const m = String(anyErr?.message || '').match(/wait_seconds"?:\s*([0-9]+)/);
            if (m) waitSeconds = parseInt(m[1], 10);
          }
          
          if (waitSeconds && Number.isFinite(waitSeconds)) {
            rateLimitUntilRef.current.set(jobId, Date.now() + waitSeconds * 1000);
            console.warn(`⚠️ Rate limit for job ${jobId}. Will retry in ${waitSeconds}s`);
          } else {
            console.error('Error processing job:', error);
          }
        } else {
          console.log('Processed batch:', data);
        }
      } catch (error) {
        console.error('Error in processing loop:', error);
      }
    }, 10000); // Try every 10s; per-job backoff prevents 429 hammering

    return () => clearInterval(interval);
  };

  const pauseJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'paused',
          pause_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job paused');
      fetchJobs();
      setProcessing(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      console.error('Error pausing job:', error);
      toast.error('Failed to pause job');
    }
  };

  const stopJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .update({
          status: 'stopped',
          end_time: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job stopped');
      fetchJobs();
      setProcessing(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      console.error('Error stopping job:', error);
      toast.error('Failed to stop job');
    }
  };

  const deleteJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('automated_generation_jobs')
        .delete()
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job deleted');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  return {
    jobs,
    loading,
    processing,
    createJob,
    startJob,
    pauseJob,
    stopJob,
    deleteJob,
    refreshJobs: fetchJobs
  };
};
